import React from 'react';
import { useTranslation } from 'react-i18next';
import LocalizedLink from '../../i18n/LocalizedLink';
import { BlogLayout } from '../../templates/BlogLayout';
import { SEOMeta } from '../../templates/SEOMeta';
import ArticleContainer from '../../components/layout/ArticleContainer';

const BlogMesaEs: React.FC = () => {
  return (
    <BlogLayout maxWidth="normal">
      <SEOMeta
        title="Modo Mesa Presencial - El Impostor"
        description="Juega a El Impostor en modo mesa presencial. Perfecto para fiestas, reuniones y eventos. Un dispositivo para todos los jugadores."
        canonical="https://el-impostor.com/blog/modalidad/mesa"
        keywords="impostor mesa, impostor presencial, impostor fiestas, impostor reuniones"
      />
      {/* Breadcrumb */}
      <div className="mb-6 text-sm text-gray-600 dark:text-gray-300">
        <LocalizedLink to="/" className="text-indigo-600 dark:text-indigo-300 hover:underline">← Inicio</LocalizedLink>
        {' '}/{' '}
        <LocalizedLink to="/blog" className="text-indigo-600 dark:text-indigo-300 hover:underline">Blog</LocalizedLink>
        {' '}/{' '}
        <LocalizedLink to="/blog/modalidad" className="text-indigo-600 dark:text-indigo-300 hover:underline">Modalidades</LocalizedLink>
        {' '}/ Mesa
      </div>

      <ArticleContainer>
        <h1 className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-purple-700 to-pink-700 bg-clip-text text-transparent mb-2">
          🎲 Modo Mesa
        </h1>
        <p className="text-lg text-purple-700 dark:text-purple-300 font-semibold mb-6">
          Un solo dispositivo, máxima interacción cara a cara
        </p>

        {/* ¿Qué es el modo mesa? */}
        <section id="que-es" className="mb-8">
          <h2 className="text-2xl font-bold text-purple-800 dark:text-purple-200 mb-4">¿Qué es el modo mesa?</h2>
          <p className="text-gray-700 dark:text-gray-200 leading-relaxed mb-4">
            El modo mesa de Impostor Game está diseñado para jugar en reuniones presenciales usando un solo dispositivo.
            Perfecto para fiestas, reuniones familiares, aulas o cualquier evento donde todos estén en el mismo lugar.
          </p>
          <div className="bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-600 dark:border-purple-500 p-4 rounded-r-lg">
            <p className="font-semibold text-purple-900 dark:text-purple-200 mb-1">✨ Principal beneficio:</p>
            <p className="text-gray-700 dark:text-gray-200">
              Disfruta de la interacción cara a cara sin distracciones digitales. 
              Solo necesitas un dispositivo (tablet, laptop o pantalla) y todos los jugadores pueden participar viéndose directamente.
              Ideal para fortalecer vínculos sociales y crear momentos memorables.
            </p>
          </div>
        </section>

        {/* Características principales */}
        <section id="caracteristicas" className="mb-8">
          <h2 className="text-2xl font-bold text-purple-800 dark:text-purple-200 mb-4">Características principales</h2>
          <ul className="list-disc pl-6 text-gray-700 dark:text-gray-200 space-y-2">
            <li><strong>Un solo dispositivo:</strong> No necesitas que cada jugador tenga su teléfono</li>
            <li><strong>Revelación de roles individual:</strong> Cada jugador ve su rol de forma privada</li>
            <li><strong>Discusión cara a cara:</strong> Debate en persona para mayor inmersión</li>
            <li><strong>Votación presencial:</strong> Vota levantando la mano o usando el dispositivo</li>
            <li><strong>Sin conexión a internet requerida:</strong> Una vez cargado, funciona offline</li>
            <li><strong>Ideal para grupos locales:</strong> Perfecto para eventos presenciales</li>
          </ul>
        </section>

        {/* Cómo jugar */}
        <section id="como-jugar" className="mb-8">
          <h2 className="text-2xl font-bold text-purple-800 dark:text-purple-200 mb-4">Cómo jugar paso a paso</h2>
          <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-200 space-y-3">
            <li><strong>Configurar la partida:</strong> Ingresa los nombres de todos los jugadores en un solo dispositivo</li>
            <li><strong>Distribuir roles:</strong> El sistema asigna roles secretos a cada jugador</li>
            <li><strong>Revelar roles:</strong> Cada jugador ve su rol de forma individual y privada</li>
            <li><strong>Debate presencial:</strong> Discutan cara a cara sobre quién puede ser el impostor</li>
            <li><strong>Votación:</strong> Cada jugador vota señalando o usando el dispositivo compartido</li>
            <li><strong>Revelación:</strong> Se revela quién fue expulsado y su rol</li>
            <li><strong>Victoria:</strong> El juego continúa hasta que se identifique al impostor o ganen los impostores</li>
          </ol>
          <div className="mt-6">
            <LocalizedLink to="/table" className="inline-block px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-colors">
              🎲 Jugar en Modo Mesa
            </LocalizedLink>
          </div>
        </section>

        {/* Casos de uso */}
        <section id="casos-uso" className="mb-8">
          <h2 className="text-2xl font-bold text-purple-800 dark:text-purple-200 mb-4">Casos de uso ideales</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-purple-200 dark:border-gray-800">
              <h3 className="font-bold text-purple-800 dark:text-purple-200 mb-2">🎉 Fiestas y reuniones</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Perfecto para animar fiestas de cumpleaños, reuniones familiares o eventos sociales</p>
            </div>
            <div className="bg-gradient-to-br from-pink-50 to-pink-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-pink-200 dark:border-gray-800">
              <h3 className="font-bold text-purple-800 dark:text-purple-200 mb-2">🏫 Aulas y educación</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Herramienta educativa para desarrollar habilidades de comunicación y pensamiento crítico</p>
            </div>
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-indigo-200 dark:border-gray-800">
              <h3 className="font-bold text-purple-800 dark:text-purple-200 mb-2">🏢 Team building</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Actividad de integración para equipos de trabajo y dinámicas empresariales</p>
            </div>
            <div className="bg-gradient-to-br from-rose-50 to-rose-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-rose-200 dark:border-gray-800">
              <h3 className="font-bold text-purple-800 dark:text-purple-200 mb-2">☕ Cafeterías y espacios sociales</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Ideal para grupos de amigos que se reúnen en persona</p>
            </div>
          </div>
        </section>

        {/* Ventajas del modo mesa */}
        <section id="ventajas" className="mb-8">
          <h2 className="text-2xl font-bold text-purple-800 dark:text-purple-200 mb-4">Ventajas del modo mesa</h2>
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-950 p-6 rounded-lg space-y-4 border border-purple-100 dark:border-gray-800">
            <div className="flex items-start">
              <span className="text-3xl mr-3">👁️</span>
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-lg">Lenguaje corporal</h3>
                <p className="text-gray-700 dark:text-gray-200">Observa gestos, miradas y reacciones en tiempo real</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-3xl mr-3">💰</span>
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-lg">Económico</h3>
                <p className="text-gray-700 dark:text-gray-200">No necesitas múltiples dispositivos o conexiones</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-3xl mr-3">🎭</span>
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-lg">Mayor inmersión</h3>
                <p className="text-gray-700 dark:text-gray-200">La interacción cara a cara hace el juego más emocionante</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-3xl mr-3">⚡</span>
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-lg">Configuración rápida</h3>
                <p className="text-gray-700 dark:text-gray-200">Comienza a jugar en menos de un minuto</p>
              </div>
            </div>
          </div>
        </section>

        {/* Consejos */}
        <section id="consejos" className="mb-8">
          <h2 className="text-2xl font-bold text-purple-800 dark:text-purple-200 mb-4">Consejos para el modo mesa</h2>
          <ul className="list-disc pl-6 text-gray-700 dark:text-gray-200 space-y-2">
            <li><strong>Usa una pantalla grande:</strong> Mejor experiencia con tablet, laptop o TV</li>
            <li><strong>Privacidad en roles:</strong> Asegúrate de que cada jugador vea su rol sin que otros miren</li>
            <li><strong>Establece límites de tiempo:</strong> Define cuánto dura cada ronda de debate</li>
            <li><strong>Ambiente adecuado:</strong> Siéntense en círculo para que todos puedan verse</li>
            <li><strong>Respeto y diversión:</strong> Mantén un ambiente amigable y divertido</li>
          </ul>
        </section>

        {/* Comparación Mesa vs Online */}
        <section id="comparacion" className="mb-8">
          <h2 className="text-2xl font-bold text-purple-800 dark:text-purple-200 mb-4">Mesa vs Online: ¿Cuál elegir?</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-sm">
              <thead className="bg-gradient-to-r from-purple-100 to-pink-100 dark:from-gray-900 dark:to-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-800 dark:text-gray-100">Aspecto</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-purple-800 dark:text-purple-200">Modo Mesa</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-blue-800 dark:text-blue-200">Modo Online</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                <tr>
                  <td className="px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200">Dispositivos necesarios</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">1 solo dispositivo</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">1 por jugador</td>
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200">Ubicación</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">Mismo lugar</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">Cualquier lugar</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200">Interacción</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">Cara a cara</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">Virtual/texto</td>
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200">Ideal para</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">Fiestas, aulas, reuniones presenciales</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">Amigos a distancia, trabajo remoto</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="mb-8">
          <h2 className="text-2xl font-bold text-purple-800 dark:text-purple-200 mb-4">Preguntas frecuentes</h2>
          <div className="space-y-4">
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">¿Necesito internet para el modo mesa?</p>
              <p className="text-gray-700 dark:text-gray-200">Solo para cargar la página inicialmente. Una vez cargado, funciona sin conexión.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">¿Cuántos jugadores pueden participar?</p>
              <p className="text-gray-700 dark:text-gray-200">Recomendamos entre 3 y 10 jugadores para una mejor experiencia.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">¿Qué dispositivo es mejor?</p>
              <p className="text-gray-700 dark:text-gray-200">Una tablet o laptop es ideal, aunque también funciona en teléfonos.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">¿Cómo se mantiene el secreto del rol?</p>
              <p className="text-gray-700 dark:text-gray-200">El juego muestra el rol de forma individual y los demás jugadores no deben mirar.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">¿Puedo usar el modo mesa en el aula?</p>
              <p className="text-gray-700 dark:text-gray-200">¡Absolutamente! Es una excelente herramienta educativa para trabajar habilidades sociales y cognitivas.</p>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <div className="mt-8 p-6 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-950 border-2 border-purple-200 dark:border-gray-800 rounded-xl">
          <h3 className="text-2xl font-semibold text-purple-900 dark:text-gray-100 mb-2">¿Listo para jugar en modo mesa?</h3>
          <p className="text-gray-700 dark:text-gray-200 mb-4">Reúne a tu grupo, prepara el dispositivo y ¡que comience la diversión cara a cara!</p>
          <div className="flex flex-wrap gap-3">
            <LocalizedLink to="/table" className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-bold hover:from-purple-700 hover:to-pink-700 transition-colors">
              🎲 Jugar en Mesa
            </LocalizedLink>
            <LocalizedLink to="/" className="px-6 py-3 text-purple-700 dark:text-purple-300 underline font-medium hover:text-purple-800 dark:hover:text-purple-200 transition-colors">
              Volver al inicio
            </LocalizedLink>
          </div>
        </div>
      </ArticleContainer>
    </BlogLayout>
  );
};

const BlogMesaPt: React.FC = () => {
  return (
    <BlogLayout maxWidth="normal">
      <SEOMeta
        title="Modo Mesa - El Impostor"
        description="Jogue El Impostor no modo mesa presencial. Um dispositivo, diversão cara a cara, sem necessidade de múltiplas conexões."
        canonical="https://el-impostor.com/blog/modalidad/mesa"
        keywords="impostor mesa, impostor presencial, jogo de mesa impostor, modo mesa impostor"
      />
      {/* Breadcrumb */}
      <div className="mb-6 text-sm text-gray-600 dark:text-gray-300">
        <LocalizedLink to="/" className="text-indigo-600 dark:text-indigo-300 hover:underline">← Início</LocalizedLink>
        {' '}/{' '}
        <LocalizedLink to="/blog" className="text-indigo-600 dark:text-indigo-300 hover:underline">Blog</LocalizedLink>
        {' '}/{' '}
        <LocalizedLink to="/blog/modalidad" className="text-indigo-600 dark:text-indigo-300 hover:underline">Modalidades</LocalizedLink>
        {' '}/ Mesa
      </div>

      <ArticleContainer>
        <h1 className="text-3xl md:text-4xl font-extrabold text-indigo-900 dark:text-indigo-200 mb-2">
          🎲 Modo Mesa
        </h1>
        <p className="text-lg text-indigo-700 dark:text-indigo-300 font-semibold mb-6">
          Um dispositivo, máxima interação presencial
        </p>

        {/* O que é o modo mesa? */}
        <section id="que-es" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">O que é o modo mesa?</h2>
          <p className="text-gray-700 dark:text-gray-200 leading-relaxed mb-4">
            O modo mesa do Impostor Game foi criado para reuniões presenciais usando um único dispositivo.
            Ideal para festas, encontros em família, salas de aula ou qualquer evento em que todos estejam juntos.
          </p>
          <div className="bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-600 p-4 rounded-r-lg">
            <p className="font-semibold text-purple-900 dark:text-purple-200 mb-1">✨ Principal benefício:</p>
            <p className="text-gray-700 dark:text-gray-200">
              Aproveite a interação cara a cara sem distrações digitais. Você só precisa de um dispositivo e todos participam juntos.
            </p>
          </div>
        </section>

        {/* Características principais */}
        <section id="caracteristicas" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Características principais</h2>
          <ul className="list-disc pl-6 text-gray-700 dark:text-gray-200 space-y-2">
            <li><strong>Um dispositivo:</strong> Os jogadores não precisam de celulares próprios</li>
            <li><strong>Revelação individual:</strong> Cada jogador vê seu papel em privado</li>
            <li><strong>Discussão presencial:</strong> Debate cara a cara para maior imersão</li>
            <li><strong>Votação presencial:</strong> Vote levantando a mão ou usando o dispositivo</li>
            <li><strong>Sem internet:</strong> Depois de carregado, pode funcionar offline</li>
            <li><strong>Ideal para grupos locais:</strong> Perfeito para encontros presenciais</li>
          </ul>
        </section>

        {/* Como jogar */}
        <section id="como-jugar" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Como jogar passo a passo</h2>
          <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-200 space-y-3">
            <li><strong>Configurar o jogo:</strong> Digite os nomes de todos os jogadores em um único dispositivo</li>
            <li><strong>Distribuir papéis:</strong> O sistema atribui papéis secretos para cada jogador</li>
            <li><strong>Revelar papéis:</strong> Cada jogador vê seu papel individualmente</li>
            <li><strong>Debate presencial:</strong> Discutam quem pode ser o impostor</li>
            <li><strong>Votação:</strong> Cada jogador vota apontando ou usando o dispositivo</li>
            <li><strong>Revelação:</strong> O jogador eliminado e seu papel são revelados</li>
            <li><strong>Vitória:</strong> O jogo continua até o impostor ser descoberto ou os impostores vencerem</li>
          </ol>
          <div className="mt-6">
            <LocalizedLink to="/table" className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors">
              Jogar no Modo Mesa
            </LocalizedLink>
          </div>
        </section>

        {/* Casos de uso */}
        <section id="casos-uso" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Casos de uso ideais</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-yellow-200 dark:border-gray-800">
              <h3 className="font-bold text-yellow-800 dark:text-yellow-200 mb-2">🎉 Festas e encontros</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Perfeito para animar aniversários, reuniões em família ou eventos sociais</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-blue-200 dark:border-gray-800">
              <h3 className="font-bold text-blue-800 dark:text-blue-200 mb-2">🏫 Sala de aula</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Ferramenta educativa para comunicação e pensamento crítico</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-green-200 dark:border-gray-800">
              <h3 className="font-bold text-green-800 dark:text-green-200 mb-2">🤝 Team building</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Atividade de integração para equipes e dinâmica de trabalho</p>
            </div>
            <div className="bg-gradient-to-br from-pink-50 to-pink-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-pink-200 dark:border-gray-800">
              <h3 className="font-bold text-pink-800 dark:text-pink-200 mb-2">☕ Cafés e espaços sociais</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Ótimo para grupos de amigos se encontrando pessoalmente</p>
            </div>
          </div>
        </section>

        {/* Vantagens */}
        <section id="ventajas" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Vantagens do modo mesa</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white dark:bg-gray-900 p-5 rounded-xl border border-gray-200 dark:border-gray-800">
              <h3 className="font-bold text-indigo-800 dark:text-indigo-200 mb-2">🧠 Linguagem corporal</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Observe gestos, olhares e reações em tempo real</p>
            </div>
            <div className="bg-white dark:bg-gray-900 p-5 rounded-xl border border-gray-200 dark:border-gray-800">
              <h3 className="font-bold text-indigo-800 dark:text-indigo-200 mb-2">💰 Custo-benefício</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Sem necessidade de vários dispositivos ou conexões</p>
            </div>
            <div className="bg-white dark:bg-gray-900 p-5 rounded-xl border border-gray-200 dark:border-gray-800">
              <h3 className="font-bold text-indigo-800 dark:text-indigo-200 mb-2">🎭 Mais imersivo</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">A interação cara a cara torna tudo mais emocionante</p>
            </div>
            <div className="bg-white dark:bg-gray-900 p-5 rounded-xl border border-gray-200 dark:border-gray-800">
              <h3 className="font-bold text-indigo-800 dark:text-indigo-200 mb-2">⚡ Configuração rápida</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Comece a jogar em menos de um minuto</p>
            </div>
          </div>
        </section>

        {/* Dicas */}
        <section id="consejos" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Dicas para o modo mesa</h2>
          <ul className="list-disc pl-6 text-gray-700 dark:text-gray-200 space-y-2">
            <li><strong>Use uma tela grande:</strong> Melhor experiência em tablet, notebook ou TV</li>
            <li><strong>Mantenha os papéis em segredo:</strong> Garanta que ninguém espreite</li>
            <li><strong>Defina limites de tempo:</strong> Decida quanto dura cada rodada de debate</li>
            <li><strong>Crie a disposição ideal:</strong> Sente-se em círculo para todos se verem</li>
            <li><strong>Respeito e diversão:</strong> Mantenha um ambiente leve e amigável</li>
          </ul>
        </section>

        {/* Comparação */}
        <section id="comparacion" className="mb-8">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Mesa vs Online: qual escolher?</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-700 dark:text-gray-200">
              <thead className="bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 uppercase text-xs">
                <tr>
                  <th className="px-4 py-2">Aspecto</th>
                  <th className="px-4 py-2 text-center">Modo Mesa</th>
                  <th className="px-4 py-2 text-center">Modo Online</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">Dispositivos necessários</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">1 dispositivo total</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">1 por jogador</td>
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">Localização</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Mesmo lugar</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Qualquer lugar</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">Interação</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Cara a cara</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Virtual/texto</td>
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">Melhor para</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Festas e encontros presenciais</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Amigos à distância, trabalho remoto</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="mb-10">
          <h2 className="text-2xl font-bold text-indigo-800 mb-4">Perguntas frequentes</h2>
          <div className="space-y-4">
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Preciso de internet para o modo mesa?</p>
              <p className="text-gray-700 dark:text-gray-200">Apenas para carregar a página inicialmente. Depois, pode funcionar offline.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Quantos jogadores podem participar?</p>
              <p className="text-gray-700 dark:text-gray-200">Recomendamos 3 a 10 jogadores para a melhor experiência.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Qual dispositivo é melhor?</p>
              <p className="text-gray-700 dark:text-gray-200">Um tablet ou notebook é ideal, mas celulares também funcionam.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Como os papéis ficam em segredo?</p>
              <p className="text-gray-700 dark:text-gray-200">O jogo revela os papéis individualmente e os outros jogadores não devem olhar.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Posso usar o modo mesa em sala de aula?</p>
              <p className="text-gray-700 dark:text-gray-200">Com certeza! É uma ótima ferramenta educativa.</p>
            </div>
          </div>
        </section>

        {/* Call-to-action */}
        <div className="bg-purple-50 dark:bg-gray-900 border border-purple-200 dark:border-gray-800 rounded-xl p-6 text-center">
          <h3 className="text-2xl font-bold text-purple-900 dark:text-purple-200 mb-2">Pronto para jogar no modo mesa?</h3>
          <p className="text-gray-700 dark:text-gray-200 mb-4">Reúna seu grupo, prepare o dispositivo e comece a diversão cara a cara!</p>
          <div className="flex flex-wrap justify-center gap-3">
            <LocalizedLink to="/table" className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors">
              Jogar no Modo Mesa
            </LocalizedLink>
            <LocalizedLink to="/" className="px-6 py-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              Voltar ao início
            </LocalizedLink>
          </div>
        </div>
      </ArticleContainer>
    </BlogLayout>
  );
};

const BlogMesaEn: React.FC = () => {
  return (
    <BlogLayout maxWidth="normal">
      <SEOMeta
        title="In-Person Table Mode - Impostor Game"
        description="Play Impostor Game in in-person table mode. Perfect for parties, gatherings, and events. One device for all players."
        canonical="https://el-impostor.com/blog/modalidad/mesa"
        keywords="impostor table mode, in person impostor, impostor parties, impostor gatherings"
      />
      {/* Breadcrumb */}
      <div className="mb-6 text-sm text-gray-600 dark:text-gray-300">
        <LocalizedLink to="/" className="text-indigo-600 dark:text-indigo-300 hover:underline">← Home</LocalizedLink>
        {' '}/{' '}
        <LocalizedLink to="/blog" className="text-indigo-600 dark:text-indigo-300 hover:underline">Blog</LocalizedLink>
        {' '}/{' '}
        <LocalizedLink to="/blog/modalidad" className="text-indigo-600 dark:text-indigo-300 hover:underline">Game modes</LocalizedLink>
        {' '}/ Table
      </div>

      <ArticleContainer>
        <h1 className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-purple-700 to-pink-700 bg-clip-text text-transparent mb-2">
          🎲 Table Mode
        </h1>
        <p className="text-lg text-purple-700 dark:text-purple-300 font-semibold mb-6">
          One device, maximum face-to-face interaction
        </p>

        {/* What is table mode? */}
        <section id="que-es" className="mb-8">
          <h2 className="text-2xl font-bold text-purple-800 dark:text-purple-200 mb-4">What is table mode?</h2>
          <p className="text-gray-700 dark:text-gray-200 leading-relaxed mb-4">
            Impostor Game’s table mode is designed for in-person gatherings using a single device.
            Perfect for parties, family meetups, classrooms, or any event where everyone is in the same place.
          </p>
          <div className="bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-600 dark:border-purple-500 p-4 rounded-r-lg">
            <p className="font-semibold text-purple-900 dark:text-purple-200 mb-1">✨ Key benefit:</p>
            <p className="text-gray-700 dark:text-gray-200">
              Enjoy face-to-face interaction without digital distractions.
              You only need one device (tablet, laptop, or a screen) and everyone can play together.
              Great for strengthening social bonds and creating memorable moments.
            </p>
          </div>
        </section>

        {/* Key features */}
        <section id="caracteristicas" className="mb-8">
          <h2 className="text-2xl font-bold text-purple-800 dark:text-purple-200 mb-4">Key features</h2>
          <ul className="list-disc pl-6 text-gray-700 dark:text-gray-200 space-y-2">
            <li><strong>One device:</strong> Not everyone needs their own phone</li>
            <li><strong>Private role reveal:</strong> Each player sees their role in private</li>
            <li><strong>Face-to-face discussion:</strong> Debate in person for more immersion</li>
            <li><strong>In-person voting:</strong> Vote by raising hands or using the shared device</li>
            <li><strong>No internet required after load:</strong> Once loaded, it can work offline</li>
            <li><strong>Perfect for local groups:</strong> Great for in-person events</li>
          </ul>
        </section>

        {/* How to play */}
        <section id="como-jugar" className="mb-8">
          <h2 className="text-2xl font-bold text-purple-800 dark:text-purple-200 mb-4">How to play step by step</h2>
          <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-200 space-y-3">
            <li><strong>Set up the match:</strong> Enter all player names on a single device</li>
            <li><strong>Assign roles:</strong> The system assigns secret roles to each player</li>
            <li><strong>Reveal roles:</strong> Each player views their role individually and privately</li>
            <li><strong>In-person debate:</strong> Discuss who could be the impostor face-to-face</li>
            <li><strong>Voting:</strong> Each player votes using the shared device (or by pointing/raising hands)</li>
            <li><strong>Reveal:</strong> The eliminated player and their role are revealed</li>
            <li><strong>Win:</strong> The game continues until the impostor is identified or impostors win</li>
          </ol>
          <div className="mt-6">
            <LocalizedLink
              to="/table"
              className="inline-block px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-colors"
            >
              🎲 Play in Table Mode
            </LocalizedLink>
          </div>
        </section>

        {/* Use cases */}
        <section id="casos-uso" className="mb-8">
          <h2 className="text-2xl font-bold text-purple-800 dark:text-purple-200 mb-4">Ideal use cases</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-purple-200 dark:border-gray-800">
              <h3 className="font-bold text-purple-800 dark:text-purple-200 mb-2">🎉 Parties and gatherings</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Perfect for birthdays, family meetups, or social events</p>
            </div>
            <div className="bg-gradient-to-br from-pink-50 to-pink-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-pink-200 dark:border-gray-800">
              <h3 className="font-bold text-purple-800 dark:text-purple-200 mb-2">🏫 Classrooms and education</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">An educational activity to develop communication and critical thinking</p>
            </div>
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-indigo-200 dark:border-gray-800">
              <h3 className="font-bold text-purple-800 dark:text-purple-200 mb-2">🏢 Team building</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">A bonding activity for teams and group dynamics</p>
            </div>
            <div className="bg-gradient-to-br from-rose-50 to-rose-100 dark:from-gray-900 dark:to-gray-800 p-5 rounded-xl border border-rose-200 dark:border-gray-800">
              <h3 className="font-bold text-purple-800 dark:text-purple-200 mb-2">☕ Cafés and social spaces</h3>
              <p className="text-sm text-gray-700 dark:text-gray-200">Great for groups of friends meeting in person</p>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section id="ventajas" className="mb-8">
          <h2 className="text-2xl font-bold text-purple-800 dark:text-purple-200 mb-4">Benefits of table mode</h2>
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-950 p-6 rounded-lg space-y-4 border border-purple-100 dark:border-gray-800">
            <div className="flex items-start">
              <span className="text-3xl mr-3">👁️</span>
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-lg">Body language</h3>
                <p className="text-gray-700 dark:text-gray-200">Watch gestures, looks, and reactions in real time</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-3xl mr-3">💰</span>
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-lg">Affordable</h3>
                <p className="text-gray-700 dark:text-gray-200">No need for multiple devices or connections</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-3xl mr-3">🎭</span>
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-lg">More immersion</h3>
                <p className="text-gray-700 dark:text-gray-200">Face-to-face interaction makes it more exciting</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-3xl mr-3">⚡</span>
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-lg">Quick setup</h3>
                <p className="text-gray-700 dark:text-gray-200">Start playing in under a minute</p>
              </div>
            </div>
          </div>
        </section>

        {/* Tips */}
        <section id="consejos" className="mb-8">
          <h2 className="text-2xl font-bold text-purple-800 dark:text-purple-200 mb-4">Tips for table mode</h2>
          <ul className="list-disc pl-6 text-gray-700 dark:text-gray-200 space-y-2">
            <li><strong>Use a larger screen:</strong> Best on a tablet, laptop, or TV</li>
            <li><strong>Role privacy:</strong> Make sure each player views their role without others looking</li>
            <li><strong>Set time limits:</strong> Decide how long each debate round lasts</li>
            <li><strong>Set the scene:</strong> Sit in a circle so everyone can see each other</li>
            <li><strong>Respect and fun:</strong> Keep it friendly and enjoyable</li>
          </ul>
        </section>

        {/* Comparison */}
        <section id="comparacion" className="mb-8">
          <h2 className="text-2xl font-bold text-purple-800 dark:text-purple-200 mb-4">Table vs Online: Which should you choose?</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-sm">
              <thead className="bg-gradient-to-r from-purple-100 to-pink-100 dark:from-gray-900 dark:to-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-800 dark:text-gray-100">Aspect</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-purple-800 dark:text-purple-200">Table mode</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-blue-800 dark:text-blue-200">Online mode</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                <tr>
                  <td className="px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200">Devices needed</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">1 device total</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">1 per player</td>
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200">Location</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">Same place</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">Anywhere</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200">Interaction</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">Face-to-face</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">Virtual / text</td>
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200">Best for</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">Parties, classrooms, in-person gatherings</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">Remote friends, distributed teams</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="mb-8">
          <h2 className="text-2xl font-bold text-purple-800 dark:text-purple-200 mb-4">Frequently asked questions</h2>
          <div className="space-y-4">
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Do I need internet for table mode?</p>
              <p className="text-gray-700 dark:text-gray-200">Only to load the page initially. Once loaded, it can work without a connection.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">How many players can participate?</p>
              <p className="text-gray-700 dark:text-gray-200">We recommend 3 to 10 players for the best experience.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">What device is best?</p>
              <p className="text-gray-700 dark:text-gray-200">A tablet or laptop is ideal, but phones can work too.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">How do you keep roles secret?</p>
              <p className="text-gray-700 dark:text-gray-200">Each player views their role individually; other players shouldn’t look.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Can I use table mode in the classroom?</p>
              <p className="text-gray-700 dark:text-gray-200">Absolutely—it’s a great educational activity for social and cognitive skills.</p>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <div className="mt-8 p-6 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-950 border-2 border-purple-200 dark:border-gray-800 rounded-xl">
          <h3 className="text-2xl font-semibold text-purple-900 dark:text-gray-100 mb-2">Ready to play in table mode?</h3>
          <p className="text-gray-700 dark:text-gray-200 mb-4">Gather your group, prepare the device, and let the face-to-face fun begin!</p>
          <div className="flex flex-wrap gap-3">
            <LocalizedLink
              to="/table"
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-bold hover:from-purple-700 hover:to-pink-700 transition-colors"
            >
              🎲 Play Table Mode
            </LocalizedLink>
            <LocalizedLink
              to="/"
              className="px-6 py-3 text-purple-700 dark:text-purple-300 underline font-medium hover:text-purple-800 dark:hover:text-purple-200 transition-colors"
            >
              Back to home
            </LocalizedLink>
          </div>
        </div>
      </ArticleContainer>
    </BlogLayout>
  );
};

const BlogMesa: React.FC = () => {
  const { i18n } = useTranslation();
  if (i18n.language === 'en') return <BlogMesaEn />;
  if (i18n.language === 'pt') return <BlogMesaPt />;
  return <BlogMesaEs />;
};

export default BlogMesa;
